import { MStockGlobalErrorUI } from '@/components/organism/mStock/MStockGlobalErrorUI'
import { queryClient } from '@queries/client.js'
import { useTranslation } from 'react-i18next'
import { useRouteError } from 'react-router-dom'
import { isAuthExpiredError } from '@utils/isAuthExpiredError.js'
import { useLayoutEffect } from 'react'
import { GlobalErrorUI } from '@components/organism/common/GlobalErrorUI.jsx'
import { HanaGlobalErrorUI } from '@components/organism/hana/HanaGlobalErrorUI.jsx'

const SERVICE_CONFIG = {
  ASP: {
    authRedirect: '/?bridge=N&companyCode=A9t7K2bQ'
  },
  MSTOCK: {
    authRedirect: '/?bridge=N&companyCode=00000011'
  },
  jb: {
    authRedirect: '/?bridge=N&companyCode=00000012'
  },
  hana: {
    authRedirect: '/?bridge=N&companyCode=00000013'
  }
}

export const ErrorPage = ({ error: boundaryError, resetErrorBoundary }) => {
  const { t } = useTranslation()
  const routeError = useRouteError()
  const error = routeError || boundaryError
  const isMStock = __APP_TARGET__ === 'MSTOCK'
  const isHana = __APP_TARGET__ === 'hana'

  const serviceConfig = SERVICE_CONFIG[__APP_TARGET__]
  const isAuthExpired = !!serviceConfig && isAuthExpiredError(error)

  const isChunkError =
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Importing a module script failed') ||
    error?.message?.includes('error loading dynamically imported module')

  const handleBoundaryRetry = () => {
    queryClient.clear()

    if (resetErrorBoundary) {
      resetErrorBoundary()
      return
    }

    window.location.reload()
  }

  const handleChunkReload = () => {
    const url = new URL(window.location.href)

    url.searchParams.set('__chunk_reload', Date.now().toString())
    window.location.replace(url.toString())
  }

  const errorActionMap = {
    retry: {
      buttonLabel: '다시 조회하기',
      handleClick: handleBoundaryRetry
    },

    home: {
      buttonLabel: '메인으로',
      handleClick: () => window.location.replace('/main')
    }
  }

  const buttonLabelMap = {
    retry: t('common.retry'),
    home: t('common.home')
  }

  const buttonAction = errorActionMap[error?.action]

  useLayoutEffect(() => {
    if (isAuthExpired) {
      window.location.replace(serviceConfig.authRedirect)
    }
  }, [isAuthExpired, serviceConfig])

  if (isAuthExpired) {
    return null
  }

  // MStock chunk
  if (isMStock && isChunkError) {
    return <MStockGlobalErrorUI buttonLabel="다시 조회하기" onRetry={handleChunkReload} />
  }

  if (isMStock && error?.status === 404) {
    return (
      <MStockGlobalErrorUI
        title="선택하신 정보를 찾을 수 없습니다."
        buttonLabel="메인으로"
        onRetry={errorActionMap.home.handleClick}
      />
    )
  }

  // MStock 기타 에러
  if (isMStock) {
    return (
      <MStockGlobalErrorUI
        buttonLabel={buttonAction?.buttonLabel}
        onRetry={buttonAction?.handleClick}
      />
    )
  }

  if (isHana && error?.code === 'NETWORK') {
    return (
      <HanaGlobalErrorUI
        title="네트워크에 연결할 수 없습니다"
        message="네트워크 연결 상태를 확인해 주세요"
        handleClick={errorActionMap.retry.handleClick}
      />
    )
  }

  if (isHana && isChunkError) {
    return (
      <HanaGlobalErrorUI
        title="네트워크에 연결할 수 없습니다"
        message="네트워크 연결 상태를 확인해 주세요"
        handleClick={handleChunkReload}
      />
    )
  }

  if (isHana) {
    return (
      <HanaGlobalErrorUI
        title="서비스를 불러올 수 없습니다"
        message="잠시 후 다시 시도해 주세요"
        handleClick={errorActionMap.retry.handleClick}
      />
    )
  }

  // chunk 에러
  if (isChunkError) {
    return (
      <GlobalErrorUI
        title={t('error.networkUnavailable.title')}
        message={t('error.networkUnavailable.message')}
        buttonLabel={buttonLabelMap.retry}
        handleClick={handleChunkReload}
      />
    )
  }

  // 네트워크 에러
  if (error?.code === 'NETWORK') {
    return (
      <GlobalErrorUI
        title={t('error.networkUnavailable.title')}
        message={t('error.networkUnavailable.message')}
        buttonLabel={buttonLabelMap.retry}
        handleClick={errorActionMap.retry.handleClick}
      />
    )
  }

  // 페이지 없음
  if (error?.status === 404) {
    return (
      <GlobalErrorUI
        title={t('error.notFound.title')}
        message={t('error.notFound.message')}
        buttonLabel={buttonLabelMap.home}
        handleClick={errorActionMap.home.handleClick}
      />
    )
  }

  return (
    <GlobalErrorUI
      title={t('error.serviceUnavailable.title')}
      message={t('error.serviceUnavailable.message')}
      buttonLabel={buttonLabelMap.retry}
      handleClick={errorActionMap.retry.handleClick}
    />
  )
}
